/**
 * Book metadata (decisions 16, 33, 62): what the front and back matter are generated from, and what EPUB's
 * package document and DOCX's properties carry. Part 05 log, tables M (fields) and X (text).
 *
 * Every field merges on its own (decision 62; {@link METADATA_MERGE}), so, as in the theme, no rule relates
 * two fields. Text goes into XML in EPUB and DOCX, so it holds only characters XML 1.0 allows (X2–X4), and never
 * a bidirectional control (X5): decision 33 has no right-to-left text, and a control in a title could make
 * an export's name or a heading read differently from what it is.
 */
import type { ImageReference } from '@cockatrice/core';
import { choiceField, frozen, integerField, parseRecordJson, readFields, RecordError } from './record.ts';
import type { Fields } from './record.ts';

/**
 * The book languages (decision 33: Latin script only), as BCP 47 tags. Each needs hyphenation patterns and
 * smart quotes (part 13). A tag is never removed once a book can hold it, and adding one is a book-format
 * change (V14): a build that doesn't know the tag refuses the whole record (decision 65).
 */
export const BOOK_LANGUAGES = frozen([
  'en-US',
  'en-GB',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'nl',
  'sv',
  'nb',
  'nn',
  'da',
  'fi',
  'pl',
  'cs',
  'ca',
] as const);

/** A book language's tag. */
export type BookLanguage = (typeof BOOK_LANGUAGES)[number];

/**
 * Part 11h: the cover colours a writer picks from (cockatrice-ui `NewBook.jsx`'s swatches), in the shelf's order: the
 * app draws colour `i` as its `--cover-i`. Like a language, a colour is never removed once a book can hold it, and
 * adding one is a book-format change (V14).
 */
export const COVER_COLOURS = frozen(['harbour', 'rust', 'linen', 'moss', 'dusk', 'ochre'] as const);

/** A cover colour's name. */
export type CoverColour = (typeof COVER_COLOURS)[number];

/** A book's metadata. Blank text means "not set"; free text holds paragraphs split by blank lines. */
export interface BookMetadata {
  readonly title: string;
  readonly subtitle: string;
  readonly author: string;
  readonly publisher: string;
  /** Who holds the copyright; blank means the author. */
  readonly copyrightHolder: string;
  readonly copyrightYear: number | null;
  /** 13 digits with a valid check digit, or empty. */
  readonly isbn: string;
  readonly language: BookLanguage;
  /** The cover image (decision 37: re-encoded to 2560 px by part 16a), as every reference names it (M17), or none. */
  readonly cover: ImageReference | null;
  /** Part 11h: the colour the writer picked for the cover drawn from the title, or null for the one its ID gives (H9). */
  readonly coverColour: CoverColour | null;
  readonly dedication: string;
  readonly epigraph: string;
  /** Who the epigraph is by. */
  readonly epigraphSource: string;
  readonly acknowledgements: string;
  readonly aboutAuthor: string;
  /** The author's other books, one title per line. */
  readonly alsoBy: string;
}

/** A text field's shape: one line, or free text with line feeds; and its longest length in UTF-16 units. */
export interface TextRule {
  readonly lines: 'one' | 'many';
  readonly max: number;
}

const line = (max: number): TextRule => ({ lines: 'one', max });
const text = (max: number): TextRule => ({ lines: 'many', max });

/** Every text field's rule (table M). */
export const METADATA_TEXT = frozen({
  title: line(300),
  subtitle: line(300),
  author: line(200),
  publisher: line(200),
  copyrightHolder: line(200),
  isbn: line(13),
  dedication: text(2_000),
  epigraph: text(2_000),
  epigraphSource: line(300),
  acknowledgements: text(20_000),
  aboutAuthor: text(20_000),
  alsoBy: text(20_000),
} as const satisfies Readonly<Record<string, TextRule>>);

/** How a merge unit's fields merge (decision 62). */
export type MergeRule = 'last-writer-wins' | 'keep-both';

/** Fields that merge together (decision 62; table U). */
export interface MergeUnit {
  readonly fields: readonly (keyof BookMetadata)[];
  readonly rule: MergeRule;
}

/**
 * How the metadata merges when two devices changed it (decision 62; table U, rows M-a to M-f). Each side is
 * compared with the base: a unit only one side changed takes that side's values for the fields it changed
 * (M-b, M-c), and the rule applies only when both sides changed the unit. Last-writer-wins units are single
 * fields, so a later write never discards a field it didn't touch (M-d). Free text is never merged silently:
 * both versions are kept and the writer picks (M-e, part 27). The epigraph and its source are one keep-both
 * unit: when both sides changed either, the writer picks a whole pair, so a quote is never credited to a
 * source written for another (M-f).
 */
export const METADATA_MERGE: readonly MergeUnit[] = frozen([
  { fields: ['title'], rule: 'last-writer-wins' },
  { fields: ['subtitle'], rule: 'last-writer-wins' },
  { fields: ['author'], rule: 'last-writer-wins' },
  { fields: ['publisher'], rule: 'last-writer-wins' },
  { fields: ['copyrightHolder'], rule: 'last-writer-wins' },
  { fields: ['copyrightYear'], rule: 'last-writer-wins' },
  { fields: ['isbn'], rule: 'last-writer-wins' },
  { fields: ['language'], rule: 'last-writer-wins' },
  { fields: ['cover'], rule: 'last-writer-wins' },
  { fields: ['coverColour'], rule: 'last-writer-wins' },
  { fields: ['dedication'], rule: 'keep-both' },
  { fields: ['epigraph', 'epigraphSource'], rule: 'keep-both' },
  { fields: ['acknowledgements'], rule: 'keep-both' },
  { fields: ['aboutAuthor'], rule: 'keep-both' },
  { fields: ['alsoBy'], rule: 'keep-both' },
]);

/** A copyright year's bounds. */
export const COPYRIGHT_YEARS = frozen({ min: 1000, max: 9999 } as const);

/** The fields' names in serialised order. */
export const METADATA_KEYS: readonly (keyof BookMetadata)[] = frozen([
  'title',
  'subtitle',
  'author',
  'publisher',
  'copyrightHolder',
  'copyrightYear',
  'isbn',
  'language',
  'cover',
  'coverColour',
  'dedication',
  'epigraph',
  'epigraphSource',
  'acknowledgements',
  'aboutAuthor',
  'alsoBy',
]);

/**
 * The first character `text` may not hold under `rule` (table X), as its code point, or null. X1: a line
 * feed, only in free text. X2: other C0 controls, DEL and C1 controls, and U+2028 and U+2029 (line and
 * paragraph separators: free text breaks lines with a line feed only). X3: a lone surrogate. X4: U+FFFE,
 * U+FFFF. X5: bidirectional embeddings, overrides and isolates (U+202A–U+202E, U+2066–U+2069) and the
 * implicit marks (U+200E, U+200F, U+061C).
 */
export function forbiddenCharacter(value: string, rule: TextRule): number | null {
  // Iterating a string yields a surrogate pair as one two-unit character and a lone surrogate as one unit.
  for (const character of value) {
    if (character.length === 2) continue;
    const unit = character.charCodeAt(0);
    if (unit === 0x0a && rule.lines === 'many') continue;
    if (
      unit < 0x20 ||
      (unit >= 0x7f && unit <= 0x9f) ||
      (unit >= 0xd800 && unit <= 0xdfff) ||
      unit === 0x2028 ||
      unit === 0x2029 ||
      unit === 0xfffe ||
      unit === 0xffff ||
      (unit >= 0x202a && unit <= 0x202e) ||
      (unit >= 0x2066 && unit <= 0x2069) ||
      unit === 0x200e ||
      unit === 0x200f ||
      unit === 0x061c
    ) {
      return unit;
    }
  }
  return null;
}

/** Every character that breaks a line (N1). */
const LINE_BREAKS: ReadonlySet<string> = new Set(['\n', '\r', '\v', '\f', '\u0085', '\u2028', '\u2029']);

/**
 * Text as a writer pasted or an importer read it, made storable under `rule` (table N): every kind of line
 * break (CR LF, CR, VT, FF, NEL, U+2028, U+2029) becomes a line feed in free text and a space in a one-line
 * field (N1), a tab becomes a space (N2), and every other character table X refuses is dropped (N3: controls,
 * bidirectional marks, U+FFFE, U+FFFF, lone surrogates). Everything else is kept as it is (N4), so the result
 * always passes {@link forbiddenCharacter} and normalising again changes nothing. Length and blankness are
 * not its business: the settings screen shows the limit.
 */
export function normalizeText(value: string, rule: TextRule): string {
  let out = '';
  // CR LF is one break.
  for (const character of value.replaceAll('\r\n', '\n')) {
    if (LINE_BREAKS.has(character)) out += rule.lines === 'many' ? '\n' : ' ';
    else if (character === '\t') out += ' ';
    else if (forbiddenCharacter(character, rule) === null) out += character;
  }
  return out;
}

function textField(fields: Fields, key: keyof typeof METADATA_TEXT): string {
  const value = fields.get(key);
  const rule = METADATA_TEXT[key];
  if (typeof value !== 'string') throw new RecordError(key, 'must be a string');
  if (value.length > rule.max) throw new RecordError(key, `is longer than ${rule.max}`);
  const bad = forbiddenCharacter(value, rule);
  if (bad !== null) throw new RecordError(key, `may not hold U+${bad.toString(16).toUpperCase().padStart(4, '0')}`);
  return value;
}

/** The ISBN-13 check digit for its first twelve digits (weights 1, 3, 1, 3, …). */
function isbn13Check(body: string): number {
  let sum = 0;
  for (let i = 0; i < 12; i += 1) sum += Number(body[i]) * (i % 2 === 0 ? 1 : 3);
  return (10 - (sum % 10)) % 10;
}

/** Whether `digits` is an ISBN-13: 978 or 979, ten more digits, and a matching check digit (M9). */
export function isIsbn13(digits: string): boolean {
  return /^97[89]\d{10}$/u.test(digits) && isbn13Check(digits) === Number(digits[12]);
}

/**
 * An ISBN as a writer types it (hyphens and spaces anywhere, ISBN-10 with a trailing X allowed) to the 13
 * digits a record stores, or null when it isn't a valid ISBN-10 or ISBN-13 (M10).
 */
export function normalizeIsbn(input: string): string | null {
  const compact = input.replace(/[\s-]/gu, '').toUpperCase();
  if (isIsbn13(compact)) return compact;
  if (!/^\d{9}[\dX]$/u.test(compact)) return null;
  // ISBN-10: weights 10 down to 2, and a check digit (X for 10) making the sum a multiple of 11.
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += Number(compact[i]) * (10 - i);
  const check = compact[9] === 'X' ? 10 : Number(compact[9]);
  if ((11 - (sum % 11)) % 11 !== check) return null;
  const body = `978${compact.slice(0, 9)}`;
  return `${body}${isbn13Check(body)}`;
}

/**
 * The metadata a new book starts with: its title and US English, everything else unset. A title the record
 * can't hold (blank, too long, a forbidden character) throws `RecordError` (W1).
 */
export function newMetadata(title: string): BookMetadata {
  return parseMetadata({
    title,
    subtitle: '',
    author: '',
    publisher: '',
    copyrightHolder: '',
    copyrightYear: null,
    isbn: '',
    language: 'en-US',
    cover: null,
    coverColour: null,
    dedication: '',
    epigraph: '',
    epigraphSource: '',
    acknowledgements: '',
    aboutAuthor: '',
    alsoBy: '',
  });
}

const IMAGE_KEYS = ['id', 'sha256'];

/** Lowercase hex of exactly `digits` digits at `path` (M17). */
function hexField(fields: Fields, key: string, digits: number): string {
  const value = fields.get(key);
  if (typeof value !== 'string' || value.length !== digits || !/^[0-9a-f]*$/u.test(value)) {
    throw new RecordError(`cover.${key}`, `must be ${digits} lowercase hex digits`);
  }
  return value;
}

/** The cover: null, or an image reference with exactly its two fields (M17, V15). */
function coverField(fields: Fields): ImageReference | null {
  const value = fields.get('cover');
  if (value === null) return null;
  const image = readFields(value, IMAGE_KEYS, 'cover', 'cover');
  return { id: hexField(image, 'id', 32), sha256: hexField(image, 'sha256', 64) };
}

/**
 * Part 11h, MC1: a record written before the cover colour existed has no `coverColour`; it reads as null (the colour its
 * ID gives), so no stored book or bundle needs rewriting. Only that key may be absent; any other missing key is refused.
 */
const ABSENT: ReadonlyMap<string, unknown> = new Map([['coverColour', null]]);

/** Part 11h: the cover colour: null, or one of {@link COVER_COLOURS} (a later build's is refused, V14). */
function coverColourField(fields: Fields): CoverColour | null {
  return fields.get('coverColour') === null ? null : choiceField(fields, 'coverColour', COVER_COLOURS);
}

/** A metadata record's value to {@link BookMetadata}, or a `RecordError` naming the first field that is wrong. */
export function parseMetadata(value: unknown): BookMetadata {
  const fields = readFields(value, METADATA_KEYS, 'metadata', null, ABSENT);
  const title = textField(fields, 'title');
  if (title.trim() === '') throw new RecordError('title', 'must not be blank');
  const year = fields.get('copyrightYear');
  const isbn = textField(fields, 'isbn');
  if (isbn !== '' && !isIsbn13(isbn)) throw new RecordError('isbn', 'must be empty or a valid ISBN-13');
  return {
    title,
    subtitle: textField(fields, 'subtitle'),
    author: textField(fields, 'author'),
    publisher: textField(fields, 'publisher'),
    copyrightHolder: textField(fields, 'copyrightHolder'),
    copyrightYear: year === null ? null : integerField(fields, 'copyrightYear', COPYRIGHT_YEARS.min, COPYRIGHT_YEARS.max),
    isbn,
    language: choiceField(fields, 'language', BOOK_LANGUAGES),
    cover: coverField(fields),
    coverColour: coverColourField(fields),
    dedication: textField(fields, 'dedication'),
    epigraph: textField(fields, 'epigraph'),
    epigraphSource: textField(fields, 'epigraphSource'),
    acknowledgements: textField(fields, 'acknowledgements'),
    aboutAuthor: textField(fields, 'aboutAuthor'),
    alsoBy: textField(fields, 'alsoBy'),
  };
}

/**
 * Metadata as JSON, fields in {@link METADATA_KEYS} order. It reads the fields by name into a fresh object,
 * the cover's too (W2: a UI store's proxy, a frozen constant or a class instance all serialise alike), checks
 * that with {@link parseMetadata} and throws its `RecordError`, so nothing is written that can't be read back
 * (W1).
 */
export function serializeMetadata(metadata: BookMetadata): string {
  const { cover } = metadata;
  const copy = Object.fromEntries(METADATA_KEYS.map((key) => [key, metadata[key]]));
  copy['cover'] = cover === null ? null : { id: cover.id, sha256: cover.sha256 };
  // parseMetadata builds its result in METADATA_KEYS order, and the cover's fields in id, sha256 order.
  return JSON.stringify(parseMetadata(copy));
}

/** Serialised metadata text to {@link BookMetadata}; `RecordError` for anything {@link serializeMetadata} can't have written. */
export function metadataFromJson(textValue: string): BookMetadata {
  return parseMetadata(parseRecordJson(textValue, 'metadata'));
}
